import Card from "./Card";
import { useId, useState } from "react";
import { useComparison } from "../hooks/useComparison";
import { useMoviesInCommon } from "../hooks/useMoviesInCommon";
import TasteCompatibility, {
  TasteCompatibilitySkeleton,
} from "./TasteCompatibility";

interface CompareWithUserProps {
  baseUsername: string;
  baseDisplayName?: string | undefined;
}

// The /compare page's meter and anchor films, with the base user fixed.
const CompareWithUser = ({
  baseUsername,
  baseDisplayName,
}: CompareWithUserProps) => {
  const { usernames } = useComparison();
  const [selected, setSelected] = useState("");
  const selectId = useId();
  const { data, loading, error } = useMoviesInCommon(
    baseUsername,
    selected || null,
  );

  const candidates = usernames.filter((u) => u.username !== baseUsername);

  const selectedDisplayName = usernames.find(
    (u) => u.username === selected,
  )?.displayName;

  return (
    <div className="space-y-4">
      <Card>
        <h4 className="text-xl font-semibold text-letterboxd-text-primary mb-3">
          Compare taste with…
        </h4>
        <div className="select-wrapper max-w-sm">
          <label htmlFor={selectId} className="sr-only">
            Select a user
          </label>
          <select
            id={selectId}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="input-field w-full"
          >
            <option value="">Select a user</option>
            {candidates.map((u) => (
              <option key={u.username} value={u.username}>
                {u.displayName || u.username}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {error && (
        <Card role="alert" className="text-letterboxd-error text-sm">
          {error}
        </Card>
      )}

      {loading && <TasteCompatibilitySkeleton />}

      {!loading && data && data.moviesInCommon.length === 0 && (
        <Card className="text-letterboxd-text-muted text-sm text-center">
          No films in common with {selectedDisplayName || selected} yet.
        </Card>
      )}

      {!loading && data && data.moviesInCommon.length > 0 && (
        <TasteCompatibility
          user1Data={{
            username: baseUsername,
            ...(baseDisplayName ? { displayName: baseDisplayName } : {}),
          }}
          user2Data={{
            username: selected,
            ...(selectedDisplayName
              ? { displayName: selectedDisplayName }
              : {}),
          }}
          moviesInCommon={data.moviesInCommon}
          compatibility={data.compatibility}
        />
      )}
    </div>
  );
};

export default CompareWithUser;
